import React, { useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Table } from 'design-system-web';
import { AdminPage } from 'components';
import { H3 } from 'components/typography';

import type { InspectionsChannel } from '../types/channel';
import { useGetInspectionTemplates } from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';
import styles from './InspectionsTemplateLibrary.scss';
import {
  type TemplateLibraryTableRow,
  mapTemplateToTableRow,
  getTemplateLibraryColumns,
  getTemplateLibraryRowActions,
} from './templateLibraryTableConfig';
import { useCustomizeInspectionTemplate } from '../hooks/useCustomizeInspectionTemplate';

type Props = {
  channel: InspectionsChannel;
  /** Channel admin route prefix (e.g. match.path from portal), without trailing slash */
  channelBasePath: string;
};

export function InspectionsTemplateLibrary({
  channel,
  channelBasePath,
}: Props) {
  const { t } = useTranslation();
  const { templates, loading, error, fetchInspectionTemplates, called } =
    useGetInspectionTemplates();
  const { customizeTemplate } = useCustomizeInspectionTemplate({
    channel,
    channelBasePath,
  });

  useEffect(() => {
    if (!channel?._id) {
      return;
    }

    const request = fetchInspectionTemplates({
      buildingId: channel._id,
      entityType: 'building',
      sourceType: SourceType.SourceTypeActivate,
    });

    // This screen relies on hook state for error UI; attach a handler so the effect does not leave a rejected promise.
    if (request) {
      request.catch(() => undefined);
    }
  }, [channel?._id, fetchInspectionTemplates]);

  const rows = useMemo(() => templates.map(mapTemplateToTableRow), [templates]);

  const templatesById = useMemo(
    () => new Map(templates.map(tpl => [tpl.id, tpl])),
    [templates]
  );

  const filterTemplatesByName = useCallback(
    (row: TemplateLibraryTableRow, keyword: string) => {
      const q = keyword.trim().toLowerCase();

      if (!q) {
        return true;
      }

      return row.name.toLowerCase().includes(q);
    },
    []
  );

  const columns = useMemo(() => getTemplateLibraryColumns(t), [t]);

  const handleCustomize = useCallback(
    (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      customizeTemplate(template);
    },
    [templatesById, customizeTemplate]
  );

  const rowActions = useMemo(
    () => getTemplateLibraryRowActions(t, { onCustomize: handleCustomize }),
    [t, handleCustomize]
  );

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.templateLibrary}>
          <div className={styles.header}>
            <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
          </div>
          <p>{t('web.admin.inspections.templateLibrary.error')}</p>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.templateLibrary}>
        <div className={styles.header}>
          <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
        </div>
        <Table<TemplateLibraryTableRow>
          columns={columns}
          data={rows}
          isLoading={loading || !called}
          pagination="client"
          defaultPageSize={25}
          rowActions={rowActions}
          rowActionsLabel={t(
            'web.admin.inspections.templateLibrary.actions.label'
          )}
          emptyMessage={t('web.admin.inspections.templateLibrary.emptyState')}
          hasKeywordFilter
          keywordFilterLabel={t(
            'web.admin.inspections.templateLibrary.searchLabel'
          )}
          customKeywordFilterFn={filterTemplatesByName}
          getRowId={row => row.id}
          showColumnVisibility
          tableKey="inspections-template-library"
        />
      </div>
    </AdminPage>
  );
}
